import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Loader2, Car, Utensils, Package, Users, Calendar, 
  DollarSign, MapPin, Clock, ChevronDown, ChevronUp, Download, FileText
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { toast } from 'sonner';

const categoryIcons = {
  transport: { icon: Car, color: 'text-blue-500', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  meals: { icon: Utensils, color: 'text-orange-500', bgColor: 'bg-orange-100 dark:bg-orange-900/30' },
  other: { icon: Package, color: 'text-purple-500', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
};

const DayLogistics = ({ day, index }) => {
  const [isOpen, setIsOpen] = useState(index === 0);
  const date = parseISO(day.date);
  const hasItems = day.transport.items.length > 0 || day.meals.items.length > 0 || day.other.items.length > 0;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-slate-200 dark:border-slate-700">
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
                  <span className="font-bold text-orange-600 dark:text-orange-400">{index + 1}</span>
                </div>
                <div>
                  <CardTitle className="text-lg dark:text-white">
                    {format(date, "EEEE", { locale: ptBR })}
                  </CardTitle>
                  <CardDescription>
                    {format(date, "dd 'de' MMMM", { locale: ptBR })}
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {day.attendee_count} pessoa(s)
                  </span>
                </div>
                {day.day_total_cost > 0 && (
                  <Badge variant="outline" className="font-semibold">
                    R$ {day.day_total_cost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Badge>
                )}
                {isOpen ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            {/* Attendees */}
            {day.attendees.length > 0 && (
              <div className="flex flex-wrap gap-2 pb-3 border-b border-slate-100 dark:border-slate-800">
                {day.attendees.map((attendee, i) => (
                  <div key={i} className="flex items-center gap-1 px-2 py-1 bg-slate-50 dark:bg-slate-800 rounded-full">
                    <span className="text-sm">{attendee.avatar}</span>
                    <span className="text-xs text-slate-600 dark:text-slate-400">{attendee.name}</span>
                  </div>
                ))}
              </div>
            )}

            {!hasItems ? (
              <p className="text-sm text-slate-400 text-center py-4">
                Nenhuma atividade planejada para este dia
              </p>
            ) : (
              <div className="grid gap-4 md:grid-cols-3">
                {/* Transport */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded-lg ${categoryIcons.transport.bgColor}`}>
                      <Car className={`w-4 h-4 ${categoryIcons.transport.color}`} />
                    </div>
                    <span className="font-medium text-slate-700 dark:text-slate-300">Transporte</span>
                    {day.transport.total_cost > 0 && (
                      <Badge variant="secondary" className="ml-auto text-xs">
                        R$ {day.transport.total_cost.toFixed(2)}
                      </Badge>
                    )}
                  </div>
                  {day.transport.items.length === 0 ? (
                    <p className="text-xs text-slate-400 pl-8">-</p>
                  ) : (
                    <div className="space-y-1 pl-8">
                      {day.transport.items.map((item, i) => (
                        <div key={i} className="text-sm">
                          <p className="font-medium text-slate-700 dark:text-slate-300">{item.title}</p>
                          {item.time && (
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {item.time}
                            </p>
                          )}
                          {item.location && (
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {item.location}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Meals */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded-lg ${categoryIcons.meals.bgColor}`}>
                      <Utensils className={`w-4 h-4 ${categoryIcons.meals.color}`} />
                    </div>
                    <span className="font-medium text-slate-700 dark:text-slate-300">Refeições</span>
                    {day.meals.total_cost > 0 && (
                      <Badge variant="secondary" className="ml-auto text-xs">
                        R$ {day.meals.total_cost.toFixed(2)}
                      </Badge>
                    )}
                  </div>
                  {day.meals.items.length === 0 ? (
                    <p className="text-xs text-slate-400 pl-8">-</p>
                  ) : (
                    <div className="space-y-1 pl-8">
                      {day.meals.items.map((item, i) => (
                        <div key={i} className="text-sm">
                          <p className="font-medium text-slate-700 dark:text-slate-300">{item.title}</p>
                          {item.time && (
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {item.time}
                            </p>
                          )}
                          {item.location && (
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {item.location}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Other */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded-lg ${categoryIcons.other.bgColor}`}>
                      <Package className={`w-4 h-4 ${categoryIcons.other.color}`} />
                    </div>
                    <span className="font-medium text-slate-700 dark:text-slate-300">Outros</span>
                    {day.other.total_cost > 0 && (
                      <Badge variant="secondary" className="ml-auto text-xs">
                        R$ {day.other.total_cost.toFixed(2)}
                      </Badge>
                    )}
                  </div>
                  {day.other.items.length === 0 ? (
                    <p className="text-xs text-slate-400 pl-8">-</p>
                  ) : (
                    <div className="space-y-1 pl-8">
                      {day.other.items.map((item, i) => (
                        <div key={i} className="text-sm">
                          <p className="font-medium text-slate-700 dark:text-slate-300">{item.title}</p>
                          {item.time && (
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {item.time}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export const LogisticsReport = ({ tripId }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchLogisticsReport();
  }, [tripId]);

  const fetchLogisticsReport = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/trips/${tripId}/logistics-report`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching logistics report:', error);
      if (error.response?.status === 403) {
        // Not organizer - this is expected
        setData(null);
      } else {
        toast.error('Erro ao carregar relatório');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const response = await api.get(`/trips/${tripId}/logistics-report/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'logistica.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF baixado com sucesso!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erro ao baixar PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">
            Relatório disponível apenas para organizadores
          </p>
        </CardContent>
      </Card>
    );
  }

  if (data.total_days === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">
            Defina as datas da viagem para ver o relatório de logística
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
                <Package className="w-5 h-5" />
                Relatório de Logística
              </CardTitle>
              <CardDescription className="text-orange-600 dark:text-orange-300">
                {data.trip_name} • {data.total_days} dias • {data.total_members} participantes
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="rounded-full border-orange-300 text-orange-700 hover:bg-orange-100 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-900/40"
              data-testid="download-logistics-pdf-btn"
            >
              {downloadingPdf ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Baixar PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-white dark:bg-slate-800 rounded-lg text-center">
              <Car className="w-5 h-5 mx-auto mb-1 text-blue-500" />
              <p className="text-xs text-slate-500 dark:text-slate-400">Transporte</p>
              <p className="font-bold text-slate-900 dark:text-white">
                R$ {data.summary.total_transport.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="p-3 bg-white dark:bg-slate-800 rounded-lg text-center">
              <Utensils className="w-5 h-5 mx-auto mb-1 text-orange-500" />
              <p className="text-xs text-slate-500 dark:text-slate-400">Refeições</p>
              <p className="font-bold text-slate-900 dark:text-white">
                R$ {data.summary.total_meals.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="p-3 bg-white dark:bg-slate-800 rounded-lg text-center">
              <Package className="w-5 h-5 mx-auto mb-1 text-purple-500" />
              <p className="text-xs text-slate-500 dark:text-slate-400">Outros</p>
              <p className="font-bold text-slate-900 dark:text-white">
                R$ {data.summary.total_other.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="p-3 bg-white dark:bg-slate-800 rounded-lg text-center border-2 border-orange-300 dark:border-orange-700">
              <DollarSign className="w-5 h-5 mx-auto mb-1 text-green-500" />
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Geral</p>
              <p className="font-bold text-green-600 dark:text-green-400">
                R$ {data.summary.grand_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {data.days.map((day, index) => (
          <DayLogistics key={day.date} day={day} index={index} />
        ))}
      </div>
    </div>
  );
};

export default LogisticsReport;
